import { useMemo } from 'react';
import {
  useQualityConsultantReport,
  useQualityDetails,
  useQualityHeatmap,
  useQualitySummary,
  type AllDetailsFiltersState,
  type AllDetailsSortState,
  type AttributeFilter,
  type DashboardQualitySettings,
  type HiddenCriterionRef,
  type QualityDetailsParams,
  serializeAttributeFilters,
} from '@/entities/dashboards';
import { getPeriodDateRange } from '../ui/utils';
import { type DashboardFiltersState } from '../ui/QualityFilters';
import { isCriterionHidden } from './hiddenCriteria';
import { type DashboardDrill } from './useDashboardScreenStore';

/** Предел строк списка звонков одной ячейки heatmap: одна ячейка (консультант × период) заведомо меньше. */
const DRILL_DETAILS_LIMIT = 1000;

/** Входные данные дашборда качества: фильтры, состояние разбора и настройки колонок. */
export type UseDashboardDataParams = {
  filters: DashboardFiltersState;
  activeAttributeFilters: AttributeFilter[];
  detailsSort: AllDetailsSortState | null;
  detailsFilters: AllDetailsFiltersState;
  /** Критерии, скрытые пользователем из отображения (каждый — в разрезе своего заключения). */
  hiddenCriteria: HiddenCriterionRef[];
  /** Выбранная ячейка heatmap для drill-down; `null` — разбор не активен. */
  drill: DashboardDrill;
  /** ФИО консультанта мини-отчёта; пустая строка — отчёт не запрашивается. */
  reportConsultant: string;
  allDetailsColumns: DashboardQualitySettings['allDetailsColumns'];
};

/**
 * Запросы данных дашборда качества и производные от них величины.
 *
 * Собирает query-параметры из фильтров панели, атрибутных фильтров, сортировки и колоночных
 * фильтров таблицы «Все детали», выполняет запросы агрегированной сводки, тепловой карты,
 * мини-отчёта консультанта, таблицы деталей и списка звонков выбранной ячейки (drill-down), и
 * возвращает готовые к отображению данные вместе с максимумами для нормировки графиков и
 * средними по видимым (не скрытым) критериям.
 */
export const useDashboardData = ({
  filters,
  activeAttributeFilters,
  detailsSort,
  detailsFilters,
  hiddenCriteria,
  drill,
  reportConsultant,
  allDetailsColumns,
}: UseDashboardDataParams) => {
  // Сериализованная строка для query-параметра; null — параметр не передаётся
  const attributeFiltersParam = useMemo(
    () => serializeAttributeFilters(activeAttributeFilters),
    [activeAttributeFilters],
  );

  const summaryParams = {
    mode: filters.heatmapMode,
    'consultant-name': filters.consultant || null,
    'attribute-filters': attributeFiltersParam,
    'date-from': filters.dateFrom || null,
    'date-to': filters.dateTo || null,
  };

  const {
    data: summaryData,
    isLoading: summaryLoading,
    isFetching: summaryFetching,
  } = useQualitySummary(summaryParams);

  const { data: heatmapData, isLoading: heatmapLoading } = useQualityHeatmap(summaryParams);

  const { data: reportData } = useQualityConsultantReport(
    reportConsultant
      ? {
          'consultant-name': reportConsultant,
          'attribute-filters': attributeFiltersParam,
          'date-from': filters.dateFrom || null,
          'date-to': filters.dateTo || null,
        }
      : null,
  );

  // Сериализация sort/filter-* для запроса quality-details.
  const sortParam = useMemo(() => {
    if (!detailsSort) return null;
    if (detailsSort.key === 'blockRating') {
      if (typeof detailsSort.blockId !== 'number') return null;
      return `blockRating:${detailsSort.blockId}:${detailsSort.direction}`;
    }
    return `${detailsSort.key}:${detailsSort.direction}`;
  }, [detailsSort]);

  const blockRatingParam = useMemo(() => {
    const entries = Object.entries(detailsFilters.blockRatings ?? {});
    if (entries.length === 0) return null;
    const arr = entries
      .map(([blockIdRaw, range]) => {
        const blockId = Number(blockIdRaw);
        if (!Number.isFinite(blockId)) return null;
        const item: { blockId: number; from?: number; to?: number } = { blockId };
        if (range.from !== undefined) item.from = range.from;
        if (range.to !== undefined) item.to = range.to;
        if (item.from === undefined && item.to === undefined) return null;
        return item;
      })
      .filter((v): v is { blockId: number; from?: number; to?: number } => v !== null);
    return arr.length > 0 ? JSON.stringify(arr) : null;
  }, [detailsFilters.blockRatings]);

  // Параметр `columns` для quality-details: JSON-массив `attribute_path`-ов
  // атрибутных колонок, оставленных пользователем видимыми в AllDetailsCard.
  //
  // `visibleColumns` в uiSettings хранит идентификаторы колонок DataGrid; для
  // атрибутных колонок они имеют префикс `attr_` (см. AllDetailsCard.tsx,
  // attributeCols). Фильтруем по префиксу и срезаем его, получая чистые
  // `attribute_path`-ы. Пересечение с `availableColumns` не делаем — бэкенд
  // молча игнорирует неизвестные коды, а отказ от пересечения избегает
  // кругового движения данных `availableColumns ← detailsData ← columns ←
  // availableColumns` и лишнего повторного запроса при первой загрузке.
  //
  // Пустой массив `"[]"` отправляется явно (а не пропускается), чтобы бэк
  // понимал «атрибуты не запрашиваются». Это отличается от поведения
  // `attribute-filters`, где пустой набор → параметр не уходит.
  const columnsParam = useMemo(() => {
    const visibleColumns = allDetailsColumns?.visibleColumns ?? [];
    const ATTRIBUTE_COLUMN_PREFIX = 'attr_';
    const codes = visibleColumns
      .filter((id) => id.startsWith(ATTRIBUTE_COLUMN_PREFIX))
      .map((id) => id.slice(ATTRIBUTE_COLUMN_PREFIX.length));
    return JSON.stringify(codes);
  }, [allDetailsColumns?.visibleColumns]);

  const { data: detailsData, isLoading: detailsLoading } = useQualityDetails({
    limit: 200,
    'attribute-filters': attributeFiltersParam,
    'date-from': filters.dateFrom || null,
    'date-to': filters.dateTo || null,
    sort: sortParam,
    'filter-external-id': detailsFilters.externalId || null,
    'filter-consultant-name': detailsFilters.consultantName || null,
    'filter-client-name': detailsFilters.clientName || null,
    'filter-total-rating-from': detailsFilters.totalRatingFrom ?? null,
    'filter-total-rating-to': detailsFilters.totalRatingTo ?? null,
    'filter-block-rating': blockRatingParam,
    columns: columnsParam,
  });

  // ===== Список звонков выбранной ячейки heatmap (drill-down) =====
  // Средний балл ячейки бэкенд считает по всей совокупности её оценок, а общий
  // список деталей ограничен limit — поэтому список ячейки нельзя строить из него:
  // у ячеек, чьи оценки не попали в срез, он оказывался пуст при наличии балла.
  // Список ячейки грузим отдельным запросом, суженным до её консультанта и периода,
  // — одинаково для режимов по месяцам и по неделям.
  const drillRange = useMemo(() => {
    if (!drill) return null;
    const periodRange = getPeriodDateRange(drill.period, filters.heatmapMode);
    if (!periodRange) return null;
    // Пересечение периода ячейки с периодом панели: ячейка построена только по
    // оценкам в границах панели — тем же ограничиваем и её список.
    const from = filters.dateFrom && filters.dateFrom > periodRange.from ? filters.dateFrom : periodRange.from;
    const to = filters.dateTo && filters.dateTo < periodRange.to ? filters.dateTo : periodRange.to;
    return { from, to };
  }, [drill, filters.heatmapMode, filters.dateFrom, filters.dateTo]);

  // Фамилия (первый токен ФИО «Фамилия И.О.») сужает выборку на бэке через
  // filter-consultant-name (ILIKE по частям ФИО). Точное соответствие консультанту
  // ячейки обеспечивает клиентская фильтрация по consultantName в PeriodDetailsCard —
  // серверный фильтр лишь ограничивает объём, чтобы limit не обрезал список ячейки.
  const drillConsultantSurname = drill ? drill.consultant.trim().split(/\s+/)[0] : '';

  const drillDetailsParams = useMemo<QualityDetailsParams | undefined>(() => {
    if (!drill || !drillRange) return undefined;
    return {
      limit: DRILL_DETAILS_LIMIT,
      'attribute-filters': attributeFiltersParam,
      'date-from': drillRange.from,
      'date-to': drillRange.to,
      'filter-consultant-name': drillConsultantSurname || null,
    };
  }, [drill, drillRange, attributeFiltersParam, drillConsultantSurname]);

  const {
    data: drillDetailsData,
    isLoading: drillDetailsLoading,
    isFetching: drillDetailsFetching,
    isPlaceholderData: drillDetailsIsPlaceholder,
  } = useQualityDetails(drillDetailsParams, { enabled: drill !== null });

  const drillItems = useMemo(() => drillDetailsData?.items ?? [], [drillDetailsData?.items]);
  // Пока грузится ячейка — спиннер, а не устаревший placeholder предыдущей ячейки.
  const drillLoading = drillDetailsLoading || (drillDetailsFetching && drillDetailsIsPlaceholder);

  const kpis = summaryData?.kpis ?? null;
  const blockNames = summaryData?.blockNames ?? [];
  const consultantNames = summaryData?.consultantNames ?? [];
  const detailsItems = detailsData?.items ?? [];
  const availableColumns = detailsData?.availableColumns ?? [];

  // Средние баллы по критериям без скрытых пользователем (настройка отображения) и без критериев
  // с нулевым потолком шкалы (maxScore === 0). Скрытие адресует критерий вместе с его
  // заключением-владельцем, поэтому одноимённый критерий другого заключения остаётся видимым.
  // Скрытый критерий уходит из графика «Критерии», из
  // выделения крайних критериев в сводке (слабейший/сильнейший) и из KPI «Проблемный критерий»
  // (слабейший видимый критерий). Критерий с maxScore === 0 набрать нельзя — его среднее всегда 0,
  // и без исключения он ложно всплывал бы слабейшим/проблемным. Прочие KPI (средний балл, кол-во,
  // консультанты, процент низких) от этого не зависят — их считает бэк по всем критериям.
  const visibleCriteriaAverages = useMemo(
    () =>
      (summaryData?.criteriaAverages ?? []).filter(
        (b) => !isCriterionHidden(hiddenCriteria, b.expertReportId, b.name) && b.maxScore !== 0,
      ),
    [summaryData?.criteriaAverages, hiddenCriteria],
  );

  // Максимумы для нормировки столбчатых графиков. Общий максимум карточки нужен только там, где
  // бары сравниваются между собой по абсолютной величине (распределение, динамика). График
  // критериев нормируется долей балла от максимума своей шкалы у каждого критерия
  // (см. CriteriaAveragesCard), рейтинг консультантов — долей балла консультанта от его
  // собственного максимума, поэтому общий максимум им обоим не нужен.
  const distributionMax = useMemo(
    () => Math.max(...(summaryData?.distribution ?? []).map((b) => b.count), 1),
    [summaryData?.distribution],
  );
  const trendMax = useMemo(() => Math.max(...(summaryData?.trend ?? []).map((b) => b.avg), 1), [summaryData?.trend]);

  return {
    summaryData,
    summaryLoading,
    summaryFetching,
    heatmapData,
    heatmapLoading,
    reportData,
    detailsItems,
    detailsLoading,
    availableColumns,
    kpis,
    blockNames,
    consultantNames,
    visibleCriteriaAverages,
    distributionMax,
    trendMax,
    drillItems,
    drillLoading,
  };
};
